import type { Epic, Task } from '@/features/project-board/types';

/**
 * 사람 담당자 명부 — 담당자 축의 정본.
 *
 * 하네스 문서의 `Owner`/`Worker` 칸에는 사람만 오지 않는다. AI 에이전트(실측 `claude-agent` 193건 ·
 * `codex` 84건 · `opus-worker`·`orchestrator`…)와 역할(`어드민` 25건 · `프론트` 3건)이 섞이고, 같은
 * 사람이 표기만 달리 여러 번 나온다(`jeff`/`Jeff`, `pierre`/`PIERRE`/`gost6967`/`seominsu`). 그대로
 * 두면 후보가 30개를 넘어 필터가 쓸모없어지고 "누가 얼마나 들고 있나"도 왜곡된다.
 *
 * 그래서 **명부에 있는 값만 사람으로 인정**한다(차단 목록이 아니라 허용 목록 — 새 에이전트 이름이
 * 늘어도 목록을 따라 고칠 일이 없다). `alias`는 저장소 이력의 git author 이름까지 포함한다 —
 * 문서에 이름을 안 적고 진행한 스펙은 그게 유일한 단서다.
 */
export class OwnerRoster {
  static readonly MEMBERS: ReadonlyArray<{ name: string; aliases: readonly string[] }> = [
    { name: 'jeff', aliases: ['jeff', 'jeff(김용휘)', '김용휘'] },
    { name: 'hunter', aliases: ['hunter', 'hunter.jang', 'hunterjang2'] },
    { name: 'pierre', aliases: ['pierre', 'gost6967', 'seominsu', '서민수'] },
    { name: 'hope', aliases: ['hope', 'gonigon'] },
    { name: 'tweety', aliases: ['tweety', 'seoy316', '장서윤'] },
  ];

  /**
   * QA 티켓을 실행하는 사람.
   *
   * 명부에서 뽑지 않는 이유: QA 산출물(체크리스트·테스트케이스)에는 담당자 칸이 아예 없어 색인이
   * 읽어 올 값이 없다. QA를 한 사람이 전담하는 동안은 이 상수가 정본이고, 사람이 늘어 티켓마다 달라지는
   * 날에는 문서에 칸을 만들어 색인이 읽게 한다 — 그때 이 상수가 지워질 자리다.
   */
  static readonly QA_ENGINEER = 'Max';

  /** 담당자 지정 화면이 고를 수 있는 이름 — 명부 그대로다. 여기서 다시 만들지 않는다(정본은 하나). */
  static readonly MEMBER_NAMES: readonly string[] = OwnerRoster.MEMBERS.map((member) => member.name);

  /**
   * 표기가 다른 같은 사람을 한 이름으로 접는다. 명부에 없으면 빈 문자열 — 사람이 아니라는 뜻이다.
   * 대소문자는 무시한다(`Jeff`·`PIERRE`가 따로 세어지던 원인).
   */
  static memberOf(value: string): string {
    const key = value.trim().toLowerCase();
    if (key === '') return '';
    return OwnerRoster.MEMBERS.find((member) => member.aliases.includes(key))?.name ?? '';
  }

  /** `jeff·hunter`처럼 한 칸에 둘이 적힌 경우를 사람 단위로 나눈다. 사람 아닌 값은 떨어진다. */
  static peopleOf(owner: string): string[] {
    return [...new Set(owner.split(/[·,/]/).map((value) => OwnerRoster.memberOf(value)).filter(Boolean))];
  }

  /**
   * 이 task에 붙은 사람 — 계획 표 `Owner` → 진행 원장 `Worker` → 에픽 담당자 순으로 내려간다.
   *
   * `Owner`가 비면 `Worker`를 쓴다(실측 market 624건 중 174건이 그 상태 — 계획 표에 담당자를 안 적고
   * 진행하면서 적는 spec이 많다). 둘 다 사람이 아니면(`unassigned`·`codex`) 에픽 담당자로 내려간다 —
   * 그건 PRD에 적힌 담당자이거나(안 적혔으면) 기획 문서를 만든 사람이라, 문서에 사람 이름을 한 번도
   * 안 적은 스펙에서 유일하게 남는 단서다(실측: `onnuri-payment` 등 7개 스펙이 `장서윤` 작업인데 셀에는 `codex`).
   */
  static assigneeOf(task: Pick<Task, 'owner' | 'worker'>, epic?: Pick<Epic, 'owner'>): string {
    return OwnerRoster.memberOf(task.owner) || OwnerRoster.memberOf(task.worker) || OwnerRoster.memberOf(epic?.owner ?? '');
  }

  /**
   * 에픽에 이름이 붙는 사람들 — **담당자가 맨 앞**, 그 뒤로 task 참여자.
   *
   * 담당자는 PRD에 적힌 사람이고, 안 적혔으면 색인이 저장소 이력에서 기획 문서를 만든 사람을 뽑아 준다.
   * task의 `Owner`/`Worker`와 출처가 달라 둘을 섞지 않고, 담당자가 task도 들고 있으면 한 번만 센다.
   */
  static epicPeopleOf(epic: Epic, tasks: Task[]): Array<{ name: string; count: number; lead: boolean }> {
    const participants = OwnerRoster.participantsOf(tasks, epic);
    const lead = OwnerRoster.peopleOf(epic.owner)[0] ?? '';
    if (lead === '') return participants.map((person) => ({ ...person, lead: false }));

    return [
      { name: lead, count: participants.find((person) => person.name === lead)?.count ?? 0, lead: true },
      ...participants.filter((person) => person.name !== lead).map((person) => ({ ...person, lead: false })),
    ];
  }

  /**
   * 담당자 필터가 에픽에 딸린 항목(에픽 자체·QA 티켓)에 거는 판정. 화면 '담당' 열(`epicPeopleOf`)과 같은
   * 기준이어야 얼굴이 떠 있는 에픽이 필터에서 빠지지 않는다.
   */
  static isEpicPerson(epic: Epic, tasks: Task[], name: string): boolean {
    return OwnerRoster.epicPeopleOf(epic, tasks).some((person) => person.name === name);
  }

  /**
   * 담당자 필터를 바꿀 때 고른 에픽이 그대로 유효한지 판정. 새 담당자의 에픽이 아니게 되면 선택을
   * 비운다 — 안 비우면 에픽 선택지에서 사라진 슬러그가 필터 값으로 남아, 보이는 선택지와 실제 필터링
   * 기준이 어긋난다.
   */
  static epicSlugAfterOwnerChange(epics: readonly Epic[], tasks: Task[], epicSlug: string, owner: string): string {
    if (owner === '' || epicSlug === '') return epicSlug;
    const epic = epics.find((candidate) => candidate.slug === epicSlug);
    return epic && OwnerRoster.isEpicPerson(epic, tasks, owner) ? epicSlug : '';
  }

  /** 에픽 참여자 — task 담당자를 건수 순으로. 에픽 문서에는 참여자 필드가 없어 task에서 집계한다. */
  static participantsOf(tasks: Task[], epic: Pick<Epic, 'slug' | 'owner'>): Array<{ name: string; count: number }> {
    const rows: Array<{ name: string; count: number }> = [];
    for (const task of tasks) {
      if (task.epicSlug !== epic.slug) continue;
      for (const person of OwnerRoster.peopleOf(OwnerRoster.assigneeOf(task, epic))) {
        const row = rows.find((candidate) => candidate.name === person);
        if (row) row.count += 1;
        else rows.push({ name: person, count: 1 });
      }
    }
    return rows.sort((left, right) => right.count - left.count);
  }
}
